import { Inject, Injectable } from "@angular/core";

import * as fromRoot from "#core/router-store";
import { Store, select } from "@ngrx/store";

import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import {
    catchError,
    map,
    switchMap,
    withLatestFrom,
} from "rxjs/operators";

import { SnackBarService } from "#services/snackbar.service";
import { Asset } from "@carnegie-learning/cure-types";
import { AssetsService } from "@carnegie-learning/data-access-angular-cure";
import { USER_SERVICE } from "@carnegie-learning/data-access-angular-fido";
import { AssignmentsService, StudentsService } from "@carnegie-learning/data-access-angular-passport";
import { FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { UserService } from "@carnegie-learning/data-access-fido";
import { PPAssignment as Assignment } from "cli-types";
import * as assignmentsStudentActions from "../actions/assignments-students.action";

@Injectable()
export class AssignmentsStudentsEffects {
    constructor(
        private actions$: Actions,
        private store: Store<fromRoot.CustomSerializer>,
        private studentsService: StudentsService,
        @Inject(USER_SERVICE) private userService: UserService,
        private assignmentsService: AssignmentsService,
        private snackBarService: SnackBarService,
        private assetsService: AssetsService
    ) {}

    loadStudents$ = createEffect(() =>
        // Calls service to retrieve students for a user in a particular teacher-class
        this.actions$
            .pipe(
                ofType(assignmentsStudentActions.loadCourseAssignmentsStudents)
            )
            .pipe(
                // get studentID from url
                withLatestFrom(
                    this.store.pipe(select(fromRoot.getRouterState)),
                    (action, router) => {
                        return {
                            payload: action.payload,
                            studentID: router.state.params.userID,
                            courseID: router.state.params.courseID,
                            assignmentID: router.state.params.assignmentID,
                            shortAssignableType:
                                router.state.params.shortAssignableType,
                            assetId: router.state.params.assetId,
                        };
                    }
                ),
                switchMap((params) => {
                    return this.assignmentsService.getCourseAssignmentStudents(
                        params.shortAssignableType,
                        params.assetId,
                        params.courseID,
                        params.payload
                    );
                }),
                // assignments need their own asset
                switchMap((response) => {
                    const assetIds = response.data.map(
                        (assignment) => assignment.assetId
                    );
                    const params: FeathersServicePaginationParameters<Asset> = {
                        limit: 100,
                        skip: 0,
                        filter: { "assetId[$in][]": assetIds },
                        sorting: {},
                    };
                    return this.assetsService.getAssets(params).pipe(
                        map((a) => {
                            const assets = a.data;
                            const newAssignmentData: Assignment[] =
                                response.data.map((assignment) => {
                                    assignment.asset = assets.find(
                                        (a) => a.assetId === assignment.assetId
                                    );
                                    return assignment;
                                });
                            response.data = newAssignmentData;
                            return response;
                        })
                    );
                }),
                //
                // assignments of each student attempts to get fido Profiles
                switchMap((response) => {
                    const studentsIds: string[] = [];
                    response.data.map((a) => {
                        const id = a.student.userGUID;
                        studentsIds.push(id);
                    });
                    return this.userService.getUsers(studentsIds).pipe(
                        map((students) => {
                            const updatedAssignments: Assignment[] = [];
                            response.data.forEach((a) => {
                                const foundStu = students.find(
                                    (stu) => stu.id === a.student.userGUID
                                );
                                if (foundStu) {
                                    a.student = {
                                        ...a.student,
                                        firstName: foundStu?.givenName,
                                        lastName: foundStu?.surName,
                                        eAddress: foundStu?.email,
                                    };
                                }
                                updatedAssignments.push(a);
                            });
                            response.data = updatedAssignments;
                            return response;
                        })
                    );
                }),
                map((response) =>
                    assignmentsStudentActions.loadCourseAssignmentsStudentsSuccess(
                        { payload: response }
                    )
                ),
                catchError((error) =>
                    of(
                        assignmentsStudentActions.loadCourseAssignmentsStudentsFail(
                            { payload: error }
                        )
                    )
                )
            )
    );
    deleteCourseAssignment$ = createEffect(() =>
        // Calls service to delete an assignment in a teacher-class for a particular student
        this.actions$
            .pipe(
                ofType(assignmentsStudentActions.deleteCourseAssignmentsStudent)
            )
            .pipe(
                map((action) => action.payload),
                switchMap((data) => {
                    return this.assignmentsService
                        .deleteCourseAssignmentStudent(data)
                        .pipe(
                            map((assignments) => assignments),
                            map((response) => {
                                this.snackBarService.success(
                                    "Assignment Deleted for this Student!"
                                );
                                return assignmentsStudentActions.deleteCourseAssignmentsStudentSuccess(
                                    { assignmentID: data.assignmentID }
                                );
                            }),
                            catchError((error) => {
                                this.snackBarService.error(
                                    "Assignment Delete Failed!"
                                );
                                return of(
                                    assignmentsStudentActions.deleteCourseAssignmentsStudentFail(
                                        { payload: error }
                                    )
                                );
                            })
                        );
                })
            )
    );
}
